import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { studentHasCourseName } from '../utils/studentCourses';
import { hasSuperUserAccess } from '../utils/sessionUser';
import {
  DIM_MAK_COURSE_NAME,
  DIM_MAK_MERIDIANS,
  meridianNameToSlug,
} from '../utils/dimMakMeridians';

export default function DimMakHandbookPage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const user = route?.params?.user ?? null;
  const courseId = route?.params?.courseId ?? null;
  const courseName = route?.params?.courseName ?? DIM_MAK_COURSE_NAME;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (courseId == null || user?.id == null) {
        setLoading(false);
        setError(true);
        return;
      }
      if (!hasSuperUserAccess(user) && !studentHasCourseName(user, courseName)) {
        setLoading(false);
        setError(true);
        return;
      }
      setLoading(true);
      setError(false);
      try {
        const r = await fetch(
          `${getApiUrl()}/api/courses/${encodeURIComponent(String(courseId))}/dim-mak/meridians?viewer_id=${encodeURIComponent(String(user.id))}`
        );
        if (!r.ok) {
          if (!cancelled) setError(true);
          return;
        }
        const data = await r.json();
        if (cancelled) return;
        setRecords(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [courseId, user, courseName]);

  const goBack = useCallback(() => {
    if (user && courseId != null) {
      navigation.navigate('MyCourseDetail', {
        user,
        courseId,
        courseName: courseName || DIM_MAK_COURSE_NAME,
      });
    } else {
      navigation.goBack();
    }
  }, [navigation, user, courseId, courseName]);

  const openMeridian = useCallback(
    (item) => {
      navigation.navigate(item.screen, {
        user,
        courseId,
        courseName: courseName || DIM_MAK_COURSE_NAME,
        slug: item.slug,
        meridianName: item.name,
      });
    },
    [navigation, user, courseId, courseName]
  );

  const links = useMemo(() => {
    return DIM_MAK_MERIDIANS.map((item) => {
      const record = records.find((row) => meridianNameToSlug(row.meridian_name) === item.slug);
      return {
        ...item,
        recordName: record?.meridian_name || item.name,
      };
    });
  }, [records]);

  const styles = useMemo(() => getStyles(theme), [theme]);

  return (
    <View
      style={styles.container}
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'dim-mak-handbook' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View style={styles.header} data-header="true">
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <TouchableOpacity
          style={styles.breadcrumbContainer}
          onPress={goBack}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Back to course"
        >
          <Text style={styles.breadcrumbBack} numberOfLines={1}>
            ‹ {courseName || DIM_MAK_COURSE_NAME}
          </Text>
        </TouchableOpacity>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={user} />
        </View>
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        <Text style={[styles.pageTitle, { color: theme.colors.heading }]}>Dim Mak handbook</Text>

        {loading ? (
          <View style={styles.centerBlock}>
            <ActivityIndicator color={theme.colors.heading} />
          </View>
        ) : error ? (
          <Text style={[styles.body, { color: theme.colors.text }]}>
            Handbook is not available yet. Ask an instructor to publish the Dim Mak meridians for this
            course.
          </Text>
        ) : (
          <View style={styles.bulletList}>
            {links.map((item) => (
              <TouchableOpacity
                key={item.screen}
                style={styles.bulletRow}
                onPress={() => openMeridian(item)}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel={`Open ${item.recordName}`}
              >
                <Text style={[styles.bullet, { color: theme.colors.heading }]}>•</Text>
                <Text style={[styles.bulletText, { color: theme.colors.heading }]}>
                  {item.recordName}
                </Text>
                <Text style={[styles.bulletChevron, { color: theme.colors.heading }]}>›</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </ScrollView>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

function getStyles(theme) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    hamburgerContainer: { marginRight: 15 },
    breadcrumbContainer: { flex: 1, minWidth: 0, backgroundColor: 'transparent' },
    breadcrumbBack: {
      fontSize: 17,
      fontWeight: '600',
      color: theme.colors.heading,
      backgroundColor: 'transparent',
    },
    profileIconContainer: {},
    content: { flex: 1 },
    contentContainer: {
      paddingHorizontal: 20,
      paddingTop: 12,
      paddingBottom: 40,
    },
    centerBlock: {
      paddingVertical: 32,
      alignItems: 'center',
    },
    pageTitle: {
      fontSize: 24,
      fontWeight: '700',
      marginBottom: 20,
      backgroundColor: 'transparent',
    },
    bulletList: {
      alignSelf: 'stretch',
    },
    bulletRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.isDarkMode ? 'rgba(255,255,255,0.1)' : '#e0e0e0',
      backgroundColor: 'transparent',
    },
    bullet: {
      fontSize: 18,
      marginRight: 10,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    bulletText: {
      flex: 1,
      fontSize: 16,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    bulletChevron: {
      fontSize: 22,
      fontWeight: '300',
      backgroundColor: 'transparent',
    },
    body: {
      fontSize: 15,
      lineHeight: 22,
      backgroundColor: 'transparent',
    },
  });
}
